import React, { useEffect, useState, useRef } from 'react'
import { supabase } from '../../supabaseClient'
import { getAuthItem } from '../../utils/auth'
import Swal from 'sweetalert2'
import ReceiptLayout from './ReceiptLayout'
import { buildReceiptBytes, buildStickerBytes, buildStickerTsplBytes, buildEodReportBytes } from './printerUtils'

import { offlineDB } from '../../offline/db'
import { cacheOrderTransactionsForOrders } from '../../offline/posOfflineHelpers'
import { useNetworkStatus } from '../../offline/useNetworkStatus'
import { formatPaymentLabel, normalizePaymentMethod } from '../Admin/DashboardOverview'
import { getBranchStaffContext } from '../../offline/posOfflineHelpers'

// Common BLE printer service/characteristic UUIDs
const PRINTER_SERVICE_UUIDS = [
    '000018f0-0000-1000-8000-00805f9b34fb',
    '49535343-fe7d-4ae5-8fa9-9fafd205e455',
    '6e400001-b5a3-f393-e0a9-e50e24dcca9e',
    'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
]
const PRINTER_CHAR_UUIDS = [
    '000018f0-0000-1000-8000-00805f9b34fb',
    '49535343-8841-43f4-a8d4-ecbe34729bb3',
    '6e400002-b5a3-f393-e0a9-e50e24dcca9e',
    'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f',
]

export default function TransactionPrinter({
    btDevice, setBtDevice,
    btChar, setBtChar,
    btStatus, setBtStatus,
    btPrinterName, setBtPrinterName,
    stickerBtDevice, setStickerBtDevice,
    stickerBtChar, setStickerBtChar,
    stickerBtStatus, setStickerBtStatus,
    stickerBtPrinterId, setStickerBtPrinterId,
    stickerBtPrinterName, setStickerBtPrinterName,
    printerMode, setPrinterMode,
}) {
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [branchId, setBranchId] = useState(null)
    const [branchName, setBranchName] = useState('')
    const [branchAddress, setBranchAddress] = useState('')
    const [cashierName, setCashierName] = useState('')
    const [orders, setOrders] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    const [dateFilter, setDateFilter] = useState('day')
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')
    const [statusFilter, setStatusFilter] = useState('All')

    const [selectedOrder, setSelectedOrder] = useState(null)
    const [orderItems, setOrderItems] = useState([])
    const [orderTransaction, setOrderTransaction] = useState(null)
    const [loadingDetail, setLoadingDetail] = useState(false)
    const [showDetailPanel, setShowDetailPanel] = useState(false)

    const [selectedStickerItems, setSelectedStickerItems] = useState(new Set())
    const [printView, setPrintView] = useState('receipt')
    const [stickerCommandMode, setStickerCommandMode] = useState(() => localStorage.getItem('kp_sticker_command_mode') || 'tspl')

    const [page, setPage] = useState(1)

    // Printer state
    const [isPrinting, setIsPrinting] = useState(false)

    const printAreaRef = useRef(null)
    // const receiptRef   = useRef(null)
    const stickerRef   = useRef(null)
    const autoReconnectAttemptedRef = useRef({ receipt: false, sticker: false })
    const hasLoadedOnceRef = useRef(false)

    const selectedOrderIdRef = useRef(null)
    useEffect(() => { selectedOrderIdRef.current = selectedOrder?.order_id || null }, [selectedOrder])

    const bluetoothSupported = typeof navigator !== 'undefined' && !!navigator.bluetooth
    const [receiptBtPrinterId, setReceiptBtPrinterId] = useState(() => localStorage.getItem('kp_bt_printer_id') || '')

    const isOnline = useNetworkStatus()
    const isOnlineRef = useRef(isOnline)
    useEffect(() => { isOnlineRef.current = isOnline }, [isOnline])

    useEffect(() => { initializeBranch() }, [])
    useEffect(() => { if (branchId) fetchOrders(branchId) }, [branchId, dateFilter, customStart, customEnd, statusFilter])
    useEffect(() => { setPage(1) }, [searchQuery, dateFilter, customStart, customEnd, statusFilter])
    useEffect(() => { localStorage.setItem('kp_bt_printer_id', receiptBtPrinterId) }, [receiptBtPrinterId])
    useEffect(() => { localStorage.setItem('kp_sticker_bt_printer_id', stickerBtPrinterId) }, [stickerBtPrinterId])
    useEffect(() => { localStorage.setItem('kp_sticker_bt_printer_name', stickerBtPrinterName) }, [stickerBtPrinterName])
    useEffect(() => { localStorage.setItem('kp_sticker_command_mode', stickerCommandMode) }, [stickerCommandMode])

    useEffect(() => {
        if (!branchId) return

        const refreshOrdersAndSelection = async () => {
            const freshList = await fetchOrders(branchId)
            if (selectedOrderIdRef.current && freshList) {
                const freshOrder = freshList.find(o => o.order_id === selectedOrderIdRef.current)
                if (freshOrder) handleSelectOrder(freshOrder)
            }
        }

        window.addEventListener('kaperk:sync-complete', refreshOrdersAndSelection)
        window.addEventListener('kaperk:orders-changed', refreshOrdersAndSelection)
        window.addEventListener('kaperk:order-created', refreshOrdersAndSelection)   // ← add this
        return () => {
            window.removeEventListener('kaperk:sync-complete', refreshOrdersAndSelection)
            window.removeEventListener('kaperk:orders-changed', refreshOrdersAndSelection)
            window.removeEventListener('kaperk:order-created', refreshOrdersAndSelection)   // ← and this
        }
    }, [branchId, dateFilter, customStart, customEnd, statusFilter])

    const findWritableCharacteristic = async (server) => {
        let foundChar = null
        for (const svcUuid of PRINTER_SERVICE_UUIDS) {
            if (foundChar) break
            try {
                const svc = await server.getPrimaryService(svcUuid)
                for (const charUuid of PRINTER_CHAR_UUIDS) {
                    try {
                        const c = await svc.getCharacteristic(charUuid)
                        if (c.properties.write || c.properties.writeWithoutResponse) { foundChar = c; break }
                    } catch { /* next */ }
                }
            } catch { /* next */ }
        }
        if (!foundChar) {
            const services = await server.getPrimaryServices()
            outer: for (const svc of services) {
                const chars = await svc.getCharacteristics()
                for (const c of chars) {
                    if (c.properties.write || c.properties.writeWithoutResponse) { foundChar = c; break outer }
                }
            }
        }
        return foundChar
    }

    const printerConnectionMap = {
        receipt: {
            key: 'receipt',
            label: 'Receipt',
            device: btDevice,
            char: btChar,
            status: btStatus,
            id: receiptBtPrinterId,
            name: btPrinterName,
            setDevice: setBtDevice,
            setChar: setBtChar,
            setStatus: setBtStatus,
            setId: setReceiptBtPrinterId,
            setName: setBtPrinterName,
            storageKey: 'kp_bt_printer_name',
            idStorageKey: 'kp_bt_printer_id',
        },
        sticker: {
            key: 'sticker',
            label: 'Sticker',
            device: stickerBtDevice,
            char: stickerBtChar,
            status: stickerBtStatus,
            id: stickerBtPrinterId,
            name: stickerBtPrinterName,
            setDevice: setStickerBtDevice,
            setChar: setStickerBtChar,
            setStatus: setStickerBtStatus,
            setId: setStickerBtPrinterId,
            setName: setStickerBtPrinterName,
            storageKey: 'kp_sticker_bt_printer_name',
            idStorageKey: 'kp_sticker_bt_printer_id',
        },
    }

    useEffect(() => {
        autoReconnectAttemptedRef.current.receipt = false
    }, [receiptBtPrinterId, btPrinterName])

    useEffect(() => {
        autoReconnectAttemptedRef.current.sticker = false
    }, [stickerBtPrinterId, stickerBtPrinterName])

    useEffect(() => {
        const autoReconnectPrinter = async ({ key, id, name, status, setStatus, setDevice, setChar }) => {
            if (!navigator.bluetooth || status === 'connected' || (!id && !name)) return
            if (status !== 'disconnected' || autoReconnectAttemptedRef.current[key]) return
            autoReconnectAttemptedRef.current[key] = true
            try {
                const devices = await navigator.bluetooth.getDevices()
                const saved = devices.find(d => (id && d.id === id) || (!id && d.name === name))
                if (!saved) return
                setStatus('connecting')
                const server = await saved.gatt.connect()
                saved.addEventListener('gattserverdisconnected', () => {
                    setStatus('disconnected'); setDevice(null); setChar(null)
                })
                const foundChar = await findWritableCharacteristic(server)
                if (foundChar) {
                    setDevice(saved); setChar(foundChar); setStatus('connected')
                } else {
                    setStatus('disconnected')
                }
            } catch {
                setStatus('disconnected')
            }
        }
        if (printerMode === 'bluetooth') {
            autoReconnectPrinter({
                key: 'receipt',
                id: receiptBtPrinterId,
                name: btPrinterName,
                status: btStatus,
                setStatus: setBtStatus,
                setDevice: setBtDevice,
                setChar: setBtChar,
            })
            autoReconnectPrinter({
                key: 'sticker',
                id: stickerBtPrinterId,
                name: stickerBtPrinterName,
                status: stickerBtStatus,
                setStatus: setStickerBtStatus,
                setDevice: setStickerBtDevice,
                setChar: setStickerBtChar,
            })
        }
    }, [
        printerMode,
        receiptBtPrinterId,
        btPrinterName,
        btStatus,
        setBtStatus,
        setBtDevice,
        setBtChar,
        stickerBtPrinterId,
        stickerBtPrinterName,
        stickerBtStatus,
        setStickerBtStatus,
        setStickerBtDevice,
        setStickerBtChar,
    ])
    

    const initializeBranch = async () => {
        try {
            setLoading(true)
            const staffData = await getBranchStaffContext(isOnlineRef.current)
            setBranchId(staffData.branch_id)
            setBranchName(staffData.branch_name)
        } catch (err) {
            console.error(err)
            Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setLoading(false)
        }
    }

    const fetchOrders = async (currentBranchId) => {
    try {

        if (!isOnlineRef.current) {
            const allCached = await offlineDB.ordersCache.toArray()
            const cached = allCached.filter(o => o.branch_id === currentBranchId)
            const now = new Date()
            const filtered = cached.filter(o => {
                if (statusFilter !== 'All' && o.order_status !== statusFilter.toLowerCase()) return false
                const d = new Date(o.created_at)
                if (dateFilter === 'day')   return d.toDateString() === now.toDateString()
                if (dateFilter === 'week')  { const s = new Date(now); s.setDate(now.getDate()-now.getDay()); s.setHours(0,0,0,0); return d >= s }
                if (dateFilter === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
                if (dateFilter === 'custom' && customStart && customEnd) {
                    const from = new Date(customStart); const to = new Date(customEnd); to.setHours(23,59,59,999)
                    return d >= from && d <= to
                }
                return true
            }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            setOrders(filtered)
            return filtered
        }

        if (!hasLoadedOnceRef.current) setLoading(true)
        else setRefreshing(true)
            let query = supabase.from('orders').select(`
                order_id, reference_number, receipt_number, order_status, order_source,
                fulfillment_type, subtotal, discount, tax, final_amount, created_at, completed_at,
                pos_processed_by, order_completed_by
            `).eq('branch_id', currentBranchId).order('created_at', { ascending: false })
            if (statusFilter !== 'All') query = query.eq('order_status', statusFilter.toLowerCase())
            const { data, error } = await query
            if (error) throw error
            const now = new Date()
            const filtered = (data || []).filter(o => {
                const d = new Date(o.created_at)
                if (dateFilter === 'day')   return d.toDateString() === now.toDateString()
                if (dateFilter === 'week')  { const s = new Date(now); s.setDate(now.getDate()-now.getDay()); s.setHours(0,0,0,0); return d >= s }
                if (dateFilter === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
                if (dateFilter === 'custom' && customStart && customEnd) {
                    const from = new Date(customStart); const to = new Date(customEnd); to.setHours(23,59,59,999)
                    return d >= from && d <= to
                }
                return true
            })
            setOrders(filtered)

            // Keep offline ordersCache authoritative for receipt numbers too —
            // not just the last-20 the POS heartbeat refreshes, or the
            // login-time snapshot. Merge (don't clobber) so fields written
            // elsewhere (cashier_name, client_ref, etc.) survive.
            for (const o of data || []) {
                const existing = await offlineDB.ordersCache.get(o.order_id)
                await offlineDB.ordersCache.put({ ...existing, ...o })
            }

            cacheOrderTransactionsForOrders((data || []).map(o => o.order_id))
                .catch(err => console.error('Transaction pre-cache failed:', err))

            return filtered

        } catch (err) { console.error(err); setOrders([]) }
        finally {
            setLoading(false)
            setRefreshing(false)
            hasLoadedOnceRef.current = true
        }
    }


    const handleSelectOrder = async (order) => {
        setSelectedOrder(order); setShowDetailPanel(true); setLoadingDetail(true)
        setPrintView('receipt'); setSelectedStickerItems(new Set())
        setBranchAddress(''); setCashierName('')
        try {

            if (!isOnlineRef.current) {
                const items = await offlineDB.orderItemsCache.where('order_id').equals(order.order_id).toArray()
                const txCache = await offlineDB.orderTransactionsCache.get(order.order_id)
                const active = items.filter(i => !['voided', 'void_approved'].includes(i.line_status))
                setOrderItems(active)
                setOrderTransaction(txCache?.transactions?.[0] || null)
                setSelectedStickerItems(new Set(active.map(i => i.order_item_id)))
                const cachedAddress = await offlineDB.metaCache.get('branchAddress')
                setBranchAddress(cachedAddress?.value || '')
                setCashierName(order.cashier_name || '')   // ← was: setCashierName('')
                setLoadingDetail(false)
                return
            }

            const [itemsRes, txRes, branchRes, baristaRes] = await Promise.all([
                supabase.from('order_items').select(`
                    order_item_id, item_id, quantity, item_price, line_status, void_reason,
                    menu_items (name, category, classification),
                    order_item_variants (variant_id, variant_price, item_variants (variant_name, category)),
                    order_item_modifiers (modifier_id, modifier_price, item_modifiers (modifier_name))
                `).eq('order_id', order.order_id),
                supabase.from('order_transactions')
                    .select('payment_method, transaction_amount, status').eq('order_id', order.order_id).maybeSingle(),
                supabase.from('branches')
                    .select('address')
                    .eq('branch_id', branchId)
                    .single(),
                (order.pos_processed_by || order.order_completed_by)
                    ? supabase.from('barista_info')
                        .select('first_name, last_name')
                        .eq('barista_id', order.pos_processed_by || order.order_completed_by)
                        .maybeSingle()
                    : Promise.resolve({ data: null })
            ])

            if (itemsRes.error) throw itemsRes.error

            const active = (itemsRes.data || []).filter(i => !['voided','void_approved'].includes(i.line_status))
            setOrderItems(active)
            setOrderTransaction(txRes.data || null)
            setSelectedStickerItems(new Set(active.map(i => i.order_item_id)))

            setBranchAddress(branchRes.data?.address || '')
            const b = baristaRes.data
            const resolvedCashierName = b ? `${b.first_name} ${b.last_name}` : ''
            setCashierName(resolvedCashierName)

            // persist it so it's available next time this order is opened offline
            await offlineDB.ordersCache.update(order.order_id, { cashier_name: resolvedCashierName })

        } catch (err) {
            console.error(err)
            setOrderItems([])
            setOrderTransaction(null)
        } finally {
            setLoadingDetail(false)
        }
    }

    const connectBluetooth = async (target) => {
        const connection = printerConnectionMap[target]
        if (!bluetoothSupported) {
            Swal.fire({ icon: 'warning', title: 'Not Supported', text: 'Web Bluetooth works only in Chrome/Edge on desktop or Android. Use System Print mode instead.', confirmButtonColor: '#A0826D' })
            return
        }
        try {
            connection.setStatus('connecting')
            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
                optionalServices: PRINTER_SERVICE_UUIDS
            })
            device.addEventListener('gattserverdisconnected', () => {
                connection.setStatus('disconnected'); connection.setDevice(null); connection.setChar(null)
            })
            const server = await device.gatt.connect()
            const foundChar = await findWritableCharacteristic(server)

            if (!foundChar) throw new Error('No writable characteristic found. This may not be a BLE ESC/POS printer.')

            connection.setDevice(device); connection.setChar(foundChar)
            connection.setId(device.id || '')
            connection.setName(device.name || `${connection.label} Printer`)
            localStorage.setItem(connection.idStorageKey, device.id || '')
            localStorage.setItem(connection.storageKey, device.name || `${connection.label} Printer`)
            connection.setStatus('connected')
            Swal.fire({ icon: 'success', title: 'Connected!', text: `${device.name || 'Printer'} is ready for ${connection.label.toLowerCase()} printing.`, confirmButtonColor: '#A0826D', timer: 2000 })
        } catch (err) {
            console.error('BT connect error:', err)
            if (err.name === 'NotFoundError') { connection.setStatus('disconnected'); return }
            connection.setStatus('error')
            Swal.fire({ icon: 'error', title: 'Connection Failed', text: err.message, confirmButtonColor: '#A0826D' })
        }
    }

    const disconnectBluetooth = (target) => {
        const connection = printerConnectionMap[target]
        if (connection.device?.gatt?.connected) connection.device.gatt.disconnect()
        connection.setId('')
        connection.setName('')
        connection.setDevice(null); connection.setChar(null); connection.setStatus('disconnected')
        localStorage.removeItem(connection.idStorageKey)
        localStorage.removeItem(connection.storageKey)
    }

    const ensurePrinterConnection = async (target) => {
        const connection = printerConnectionMap[target]
        if (connection.char && connection.device?.gatt?.connected) return connection.char

        if (!connection.device) throw new Error(`${connection.label} printer is not connected. Click ${connection.label} Scan above.`)

        connection.setStatus('connecting')
        const server = await connection.device.gatt.connect()
        const foundChar = await findWritableCharacteristic(server)
        if (!foundChar) throw new Error(`No writable characteristic found for the ${connection.label.toLowerCase()} printer.`)

        connection.setChar(foundChar)
        connection.setStatus('connected')
        return foundChar
    }

    const writeBtChunk = async (char, data, chunkSize = 128) => {
        for (let i = 0; i < data.length; i += chunkSize) {
            const chunk = data.slice(i, i + chunkSize)
            if (char.properties.writeWithoutResponse) await char.writeValueWithoutResponse(chunk)
            else await char.writeValue(chunk)
            await new Promise(r => setTimeout(r, 20))
        }
    }

    const handlePrint = async (type) => {
        if (printerMode === 'bluetooth') await handleBluetoothPrint(type)
        else handleSystemPrint(type)
    }

    const handleSystemPrint = (type) => {
        if (type === 'receipt') {
            document.body.classList.add('print-receipt')
            window.print()
            setTimeout(() => document.body.classList.remove('print-receipt'), 1000)
        } else {
            const printArea = printAreaRef.current
            if (!stickerRef.current || !printArea) return
            printArea.innerHTML = stickerRef.current.innerHTML
            document.body.classList.add('print-sticker')
            window.print()
            setTimeout(() => {
                document.body.classList.remove('print-sticker')
                printArea.innerHTML = ''
            }, 1000)
        }
    }

    const handleBluetoothPrint = async (type) => {
        const connection = printerConnectionMap[type]
        if (!connection.char && !connection.device) {
            Swal.fire({ icon: 'warning', title: 'No Printer Connected', text: `Connect a ${connection.label.toLowerCase()} printer using "Scan & Connect" above.`, confirmButtonColor: '#A0826D' })
            return
        }
        if (!selectedOrder) return
        setIsPrinting(true)
        try {
            const targetChar = await ensurePrinterConnection(type)
            if (type === 'receipt') {
                await writeBtChunk(targetChar, buildReceiptBytes(selectedOrder, orderItems, orderTransaction, branchName, branchAddress, cashierName))
            } else {
                const items = orderItems.filter(i => selectedStickerItems.has(i.order_item_id))
                const totalStickers = items.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 1), 0)
                for (const item of items) {
                    for (let i = 0; i < (parseInt(item.quantity, 10) || 1); i++) {
                        const stickerBytes = stickerCommandMode === 'tspl'
                            ? buildStickerTsplBytes(item, selectedOrder)
                            : buildStickerBytes(item, selectedOrder)
                        await writeBtChunk(targetChar, stickerBytes)
                        await new Promise(r => setTimeout(r, 300))
                    }
                }
                Swal.fire({ icon: 'success', title: 'Printed!', text: `${totalStickers} sticker${totalStickers !== 1 ? 's' : ''} sent to ${connection.name || 'printer'}.`, confirmButtonColor: '#A0826D', timer: 1800 })
                return
            }
            Swal.fire({ icon: 'success', title: 'Printed!', text: 'Receipt sent to printer.', confirmButtonColor: '#A0826D', timer: 1800 })
        } catch (err) {
            console.error('BT print error:', err)
            connection.setStatus('error')
            Swal.fire({ icon: 'error', title: 'Print Failed', text: err.message || 'Failed to send data to printer.', confirmButtonColor: '#A0826D' })
        } finally {
            setIsPrinting(false)
        }
    }

    // const PAYMENT_LABELS = {
    //     cash: 'Cash',
    //     gcash: 'GCash',
    //     card: 'Card',
    //     online: 'Online Payment',            // ← change this key to match what you save for online payments
    //     managers_pullout: "Manager's Pull-Out",
    //     marketing_pullout: 'Marketing Pull-Out',
    // }

    const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100
    const sumBy = (arr, k) => arr.reduce((s, x) => s + parseFloat(x[k] || 0), 0)

    // Gets (or creates) today's Z-reading row. Reprinting the same day reuses the same counter.
    const getZReading = async (businessDate, netSales) => {
        const { data: existing, error: exErr } = await supabase.from('z_readings')
            .select('*').eq('branch_id', branchId).eq('business_date', businessDate).maybeSingle()
        if (exErr) throw exErr
        if (existing) return existing

        const { data: last, error: lastErr } = await supabase.from('z_readings')
            .select('z_counter, ending_accumulated').eq('branch_id', branchId)
            .order('z_counter', { ascending: false }).limit(1).maybeSingle()
        if (lastErr) throw lastErr

        const beginning = Number(last?.ending_accumulated || 0)
        const { data: created, error: insErr } = await supabase.from('z_readings').insert({
            branch_id: branchId,
            business_date: businessDate,
            z_counter: (last?.z_counter || 0) + 1,
            beginning_accumulated: beginning,
            ending_accumulated: r2(beginning + netSales),
        }).select().single()
        if (insErr) throw insErr
        return created
    }

    const gatherEodData = async () => {
        const start = new Date(); start.setHours(0, 0, 0, 0)
        const end = new Date(start); end.setDate(end.getDate() + 1)
        const businessDate = start.toLocaleDateString('en-CA') // YYYY-MM-DD in local time

        // ── Orders ──
        const { data: o, error: oErr } = await supabase.from('orders')
            .select('order_id, order_status, fulfillment_type, subtotal, discount, final_amount, pos_processed_by, order_completed_by')
            .eq('branch_id', branchId)
            .gte('created_at', start.toISOString())
            .lt('created_at', end.toISOString())
        if (oErr) throw oErr

        const allOrders = o || []
        const refundedOrders = allOrders.filter(x => x.order_status === 'refunded')   // ← adjust if refunds are stored differently
        const counted = allOrders.filter(x => !['cancelled', 'refunded'].includes(x.order_status))
        const ids = counted.map(x => x.order_id)

        // ── Payments + voided lines (chunked) ──
        const paidTx = []
        let voids = 0
        for (let i = 0; i < ids.length; i += 50) {
            const slice = ids.slice(i, i + 50)
            const [txRes, voidRes] = await Promise.all([
                supabase.from('order_transactions').select('order_id, payment_method, transaction_amount, status').in('order_id', slice),
                supabase.from('order_items').select('quantity, item_price').in('order_id', slice).in('line_status', ['voided', 'void_approved']),
            ])
            if (txRes.error) throw txRes.error
            if (voidRes.error) throw voidRes.error
            for (const row of txRes.data || []) {
                if (['completed', 'paid'].includes(row.status)) paidTx.push(row)
            }
            for (const v of voidRes.data || []) voids += parseFloat(v.item_price || 0) * (parseInt(v.quantity, 10) || 0)
        }

        // ── Sales summary ──
        const grossSales = sumBy(counted, 'subtotal')
        const discounts = sumBy(counted, 'discount')
        const refunds = sumBy(refundedOrders, 'final_amount')
        const netSales = r2(grossSales - discounts - refunds - voids)

        // ── VAT breakdown (12% VAT-inclusive) ──
        const vatableSales = r2(netSales / 1.12)
        const vatAmount = r2(netSales - vatableSales)

        // ── Payments (dynamic: only methods actually used today) ──
        const paymentTotals = {}
        for (const t of paidTx) {
            const key = normalizePaymentMethod(t.payment_method)
            paymentTotals[key] = (paymentTotals[key] || 0) + parseFloat(t.transaction_amount || 0)
        }
        const payments = Object.entries(paymentTotals)
            .sort((a, b) => b[1] - a[1])
            .map(([key, amount]) => ({ label: formatPaymentLabel(key), amount: r2(amount) }))
        const totalPayments = r2(payments.reduce((s, p) => s + p.amount, 0))

        // ── Order types ──
        const orderTypes = { dine_in: 0, takeout: 0, pickup: 0, delivery: 0 }
        for (const ord of counted) if (ord.fulfillment_type in orderTypes) orderTypes[ord.fulfillment_type]++

        // ── Cashier activity ──
        const cashierCounts = {}
        for (const ord of counted) {
            const id = ord.pos_processed_by || ord.order_completed_by
            if (id) cashierCounts[id] = (cashierCounts[id] || 0) + 1
        }
        let cashiers = []
        const cashierIds = Object.keys(cashierCounts)
        if (cashierIds.length) {
            const { data: b, error: bErr } = await supabase.from('barista_info')
                .select('barista_id, first_name, last_name').in('barista_id', cashierIds)
            if (bErr) throw bErr
            cashiers = cashierIds.map(id => {
                const p = (b || []).find(x => x.barista_id === id)
                return { name: p ? `${p.first_name} ${p.last_name}` : 'Unknown', count: cashierCounts[id] }
            }).sort((a, b) => b.count - a.count)
        }

        // ── Cash drawer ──
        const { data: sessions, error: sErr } = await supabase.from('cash_drawer_sessions')
            .select('session_id, status, opening_amount, closing_amount')
            .eq('branch_id', branchId)
            .gte('opened_at', start.toISOString())
            .lt('opened_at', end.toISOString())
            .order('opened_at', { ascending: true })
        if (sErr) throw sErr

        let drawer = null
        if (sessions?.length) {
            const { data: dTx, error: dErr } = await supabase.from('cash_drawer_transactions')
                .select('type, amount').in('session_id', sessions.map(s => s.session_id))
            if (dErr) throw dErr
            const cashSales = paymentTotals.cash || 0
            const cashIn = (dTx || []).filter(t => t.type === 'pay_in').reduce((s, t) => s + Number(t.amount || 0), 0)
            const cashOut = (dTx || []).filter(t => t.type === 'pay_out').reduce((s, t) => s + Number(t.amount || 0), 0)
            const opening = Number(sessions[0].opening_amount || 0)
            const expected = r2(opening + cashSales + cashIn - cashOut)
            const lastS = sessions[sessions.length - 1]
            const actual = lastS.status === 'closed' && lastS.closing_amount != null ? Number(lastS.closing_amount) : null
            drawer = { opening, cashSales: r2(cashSales), cashIn, cashOut, expected, actual, variance: actual == null ? null : r2(actual - expected) }
        }

        // ── Z-reading (counter + accumulated sales) ──
        const z = await getZReading(businessDate, netSales)

        return {
            businessDate: start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            generatedAt: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
            zCounter: z.z_counter,
            totalTransactions: counted.length,
            grossSales: r2(grossSales), discounts: r2(discounts), refunds: r2(refunds), voids: r2(voids), netSales,
            vatableSales, vatAmount, vatExempt: 0, zeroRated: 0,
            payments, totalPayments, orderTypes, drawer, cashiers,
            beginningAccumulated: Number(z.beginning_accumulated),
            endingAccumulated: Number(z.ending_accumulated),
        }
    }

    const handlePrintEndOfDay = async () => {
        if (!isOnlineRef.current) {
            Swal.fire({ icon: 'warning', title: 'Internet Required', text: 'The Z-Reading needs a connection so the Z-counter stays sequential. Please reconnect and try again.', confirmButtonColor: '#A0826D' })
            return
        }

        const result = await Swal.fire({
            icon: 'question',
            title: 'Print End of Day Z-Reading?',
            text: "This will print today's Z-Reading report.",
            showCancelButton: true,
            confirmButtonText: 'Yes, Print',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#A0826D',
            cancelButtonColor: '#6b7280'
        })
        if (!result.isConfirmed) return

        if (printerMode === 'bluetooth' && !btChar && !btDevice) {
            Swal.fire({ icon: 'warning', title: 'No Printer Connected', text: 'Connect a receipt printer using "Receipt Scan" above.', confirmButtonColor: '#A0826D' })
            return
        }

        setIsPrinting(true)
        try {
            const data = await gatherEodData()
            const targetChar = await ensurePrinterConnection('receipt')
            await writeBtChunk(targetChar, buildEodReportBytes({ branchName, ...data }))
            Swal.fire({ icon: 'success', title: 'Printed!', text: `Z-Reading #${String(data.zCounter).padStart(6, '0')} sent to printer.`, confirmButtonColor: '#A0826D', timer: 1800 })
        } catch (err) {
            console.error('EOD print error:', err)
            Swal.fire({ icon: 'error', title: 'Printing Failed', text: err.message || 'Failed to print end of day report.', confirmButtonColor: '#A0826D' })
        } finally {
            setIsPrinting(false)
        }
    }

    const toggleStickerItem = (id) => {
        setSelectedStickerItems(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
    }

    const filteredOrders = orders.filter(o => {
        const q = searchQuery.toLowerCase()
        return String(o.reference_number || '').toLowerCase().includes(q) || String(o.order_status || '').toLowerCase().includes(q)
    })

    const PAGE_SIZE = 10
    const totalPages = Math.ceil(filteredOrders.length / PAGE_SIZE)
    const paginatedOrders = filteredOrders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)


    const formatDate = (v) => !v ? '—' : new Date(v).toLocaleString('en-PH', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    const formatShortDate = (v) => !v ? '—' : new Date(v).toLocaleString('en-PH', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    const formatCurrency = (v) => `₱${parseFloat(v || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    const formatFulfillment = (v) => ({ dine_in: 'Dine-In', takeout: 'Takeout', pickup: 'Pickup', delivery: 'Delivery' }[v] || v || '—')
    const statusColor = (s) => ({ pending: 'bg-yellow-100 text-yellow-800', preparing: 'bg-blue-100 text-blue-800', ready: 'bg-purple-100 text-purple-800', completed: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' }[s] || 'bg-gray-100 text-gray-700')

    const getBtStatusMeta = (status, name) => ({
        disconnected: { label: 'Disconnected', color: 'text-gray-400', dot: 'bg-gray-300' },
        connecting:   { label: 'Connecting…',  color: 'text-yellow-600', dot: 'bg-yellow-400 animate-pulse' },
        connected:    { label: name || 'Connected', color: 'text-green-600', dot: 'bg-green-500' },
        error:        { label: 'Connection error', color: 'text-red-500', dot: 'bg-red-500' },
    }[status])

    const receiptStatusMeta = getBtStatusMeta(btStatus, btPrinterName)
    const stickerStatusMeta = getBtStatusMeta(stickerBtStatus, stickerBtPrinterName)

    const printBtnLabel = isPrinting ? 'Printing…'
    : printView === 'receipt'
        ? '🖨️ Print Receipt'
        : (printerMode === 'bluetooth'
            ? ` Send ${selectedStickerItems.size} Sticker${selectedStickerItems.size !== 1 ? 's' : ''}`
            : ` Print ${selectedStickerItems.size} Sticker${selectedStickerItems.size !== 1 ? 's' : ''}`)

    return (
        <>
            <style>{`
                #tp-receipt-print { display: none; }
                #tp-sticker-print { display: none; }
                @media print {
                    body > * { display: none !important; }
                    body.print-receipt #tp-receipt-print { display: block !important; }
                    body.print-sticker #tp-sticker-print { display: block !important; }
                }
            `}</style>
            <div id="tp-sticker-print" ref={printAreaRef} />
            <div id="tp-receipt-print">
                {selectedOrder && orderItems.length > 0 && (
                    <ReceiptLayout
                        order={selectedOrder}
                        items={orderItems}
                        transaction={orderTransaction}
                        branchName={branchName}
                        branchAddress={branchAddress}
                        cashier={cashierName}
                    />
                )}
            </div>

            <div className="flex flex-col h-full bg-gray-50 overflow-hidden">

                {/* ── Printer mode toolbar ─────────────────────────── */}
                <div className="bg-white border-b border-gray-200 px-5 py-2.5 flex items-center justify-between gap-4 flex-wrap flex-shrink-0">
                    <div className="flex items-center gap-3 flex-wrap">
                        {/* Mode pills */}
                        <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs font-medium">
                            {/* <button onClick={() => setPrinterMode('system')}
                                className={`px-3 py-1.5 transition-colors ${printerMode === 'system' ? 'bg-[#303030] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                                🖨️ System Print
                            </button> */}
                            <button onClick={() => setPrinterMode('bluetooth')}
                                className={`px-3 py-1.5 transition-colors border-l border-gray-200 ${printerMode === 'bluetooth' ? 'bg-[#303030] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                                Bluetooth Direct
                            </button>
                        </div>

                        {/* Status pill */}
                        {printerMode === 'bluetooth' && (
                            <div className="flex items-center gap-3 flex-wrap">
                                {[
                                    { label: 'Receipt', meta: receiptStatusMeta },
                                    { label: 'Sticker', meta: stickerStatusMeta },
                                ].map(printer => (
                                    <div key={printer.label} className="flex items-center gap-1.5 min-w-0">
                                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${printer.meta.dot}`} />
                                        <span className={`text-xs font-medium truncate max-w-[190px] ${printer.meta.color}`} title={`${printer.label}: ${printer.meta.label}`}>
                                            {printer.label}: {printer.meta.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {printerMode === 'system' && (
                            <span className="text-xs text-gray-400">Prints using your OS default printer</span>
                        )}
                    </div>

                    {/* BT actions */}
                    {printerMode === 'bluetooth' && (
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                            {!bluetoothSupported && (
                                <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg">
                                    Chrome/Edge only
                                </span>
                            )}
                            {[
                                { key: 'receipt', label: 'Receipt', status: btStatus, meta: receiptStatusMeta, name: btPrinterName },
                                { key: 'sticker', label: 'Sticker', status: stickerBtStatus, meta: stickerStatusMeta, name: stickerBtPrinterName },
                            ].map(printer => (
                                <div key={printer.key} className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 pl-2">
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${printer.meta.dot}`} />
                                    <span className={`text-[11px] font-semibold truncate max-w-[130px] ${printer.meta.color}`} title={printer.name || printer.label}>
                                        {printer.label}{printer.name ? `: ${printer.name}` : ''}
                                    </span>
                                    {printer.status === 'connected' ? (
                                        <button onClick={() => disconnectBluetooth(printer.key)}
                                            className="px-2.5 py-1.5 text-xs font-medium bg-red-50 text-red-600 border-l border-red-100 rounded-r-lg hover:bg-red-100 transition-colors">
                                            Disconnect
                                        </button>
                                    ) : (
                                        <button onClick={() => connectBluetooth(printer.key)} disabled={!bluetoothSupported || printer.status === 'connecting'}
                                            className="px-2.5 py-1.5 text-xs font-semibold bg-[#303030] text-white rounded-r-lg hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                <path fill="currentColor" d="m17.71 7.71L12 2h-1v7.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L11 14.41V22h1l5.71-5.71l-4.3-4.29zM13 5.83l1.88 1.88L13 9.59zm1.88 10.46L13 18.17v-3.76z"/>
                                            </svg>
                                            {printer.status === 'connecting' ? 'Connecting…' : 'Scan'}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Main two-column area ─────────────────────────── */}
                <div className="flex flex-1 overflow-hidden">

                    {/* Left: order list */}
                    <div className={`flex flex-col border-r border-gray-200 bg-white transition-all duration-200 ${showDetailPanel ? 'hidden md:flex md:w-[380px] md:min-w-[280px]' : 'flex flex-1'}`}>                        <div className="px-5 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <h2 className="text-base font-bold text-gray-800">Orders</h2>
                                    <p className="text-xs text-gray-400 mt-0.5">{branchName}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {dateFilter === 'day' && printerMode === 'bluetooth' && (
                                        <button onClick={handlePrintEndOfDay} disabled={isPrinting}
                                            className="px-3 py-2 rounded-lg bg-[#303030] text-white text-xs font-semibold hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                                            title="Print all of today's receipts">
                                            Print Z-Reading
                                        </button>
                                    )}
                                    <button onClick={() => fetchOrders(branchId)}
                                        className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors" title="Refresh">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                                            <path fill="currentColor" d="M12 20q-3.35 0-5.675-2.325T4 12t2.325-5.675T12 4q1.725 0 3.3.712T18 6.75V4h2v7h-7V9h4.2q-.8-1.4-2.187-2.2T12 6Q9.5 6 7.75 7.75T6 12t1.75 4.25T12 18q1.925 0 3.475-1.1T17.65 14h2.1q-.7 2.65-2.85 4.325T12 20"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="relative">
                                    <input type="text" placeholder="Search reference #..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6"/></svg>
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <select value={dateFilter} onChange={e => { setDateFilter(e.target.value); if (e.target.value !== 'custom') { setCustomStart(''); setCustomEnd('') } }}
                                        className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#A0826D]">
                                        <option value="day">Today</option>
                                        <option value="week">This Week</option>
                                        <option value="month">This Month</option>
                                        <option value="all">All Time</option>
                                        <option value="custom">Custom</option>
                                    </select>
                                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                                        className="flex-1 px-2 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#A0826D]">
                                        <option value="All">All Status</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Preparing">Preparing</option>
                                        <option value="Ready">Ready</option>
                                        <option value="Completed">Completed</option>
                                        <option value="Cancelled">Cancelled</option>
                                    </select>
                                </div>
                                {dateFilter === 'custom' && (
                                    <div className="flex items-center gap-2">
                                        <input type="date" value={customStart} max={customEnd || new Date().toISOString().split('T')[0]} onChange={e => setCustomStart(e.target.value)}
                                            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                                        <span className="text-xs text-gray-400">to</span>
                                        <input type="date" value={customEnd} min={customStart} max={new Date().toISOString().split('T')[0]} onChange={e => setCustomEnd(e.target.value)}
                                            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                            {loading && orders.length === 0 ? (
                                <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Loading orders...</div>
                            ) : filteredOrders.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" className="mb-2 opacity-30"><path fill="currentColor" d="M18 3H6v4H5a3 3 0 0 0-3 3v6a2 2 0 0 0 2 2h2v3h12v-3h2a2 2 0 0 0 2-2v-6a3 3 0 0 0-3-3zM8 5h8v2H8zm0 14v-4h8v4zm10-7a1 1 0 1 1 0-2a1 1 0 0 1 0 2z"/></svg>
                                    <p className="text-sm">No orders found</p>
                                </div>
                            ) : (
                                paginatedOrders.map(order => (
                                    <button key={order.order_id} onClick={() => handleSelectOrder(order)}
                                        className={`w-full text-left px-5 py-3.5 hover:bg-gray-50 transition-colors ${selectedOrder?.order_id === order.order_id ? 'bg-[#FAF8F6] border-l-4 border-[#A0826D]' : 'border-l-4 border-transparent'}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-gray-800 truncate">#{order.reference_number}</p>
                                                <p className="text-xs text-gray-400 mt-0.5">{formatShortDate(order.created_at)}</p>
                                                <p className="text-xs text-gray-500 mt-0.5">{formatFulfillment(order.fulfillment_type)}</p>
                                            </div>
                                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColor(order.order_status)}`}>
                                                    {order.order_status?.charAt(0).toUpperCase() + order.order_status?.slice(1)}
                                                </span>
                                                <span className="text-sm font-bold text-gray-800">{formatCurrency(order.final_amount)}</span>
                                            </div>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>

                        <div className="px-5 py-2.5 border-t border-gray-200 bg-gray-50">
                            <div className="flex items-center justify-between">
                                <p className="text-xs text-gray-400">
                                    {filteredOrders.length === 0
                                        ? '0 orders'
                                        : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filteredOrders.length)} of ${filteredOrders.length}`}
                                </p>

                                {totalPages > 1 && (
                                    <div className="flex items-center gap-1.5">
                                        <button
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            disabled={page === 1}
                                            className="px-2.5 py-1 rounded-lg border text-[11px] border-gray-200 bg-white text-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                        >
                                            ← Prev
                                        </button>

                                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                                            .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                                            .reduce((acc, n, idx, arr) => {
                                                if (idx > 0 && n - arr[idx - 1] > 1) acc.push('…')
                                                acc.push(n)
                                                return acc
                                            }, [])
                                            .map((n, idx) =>
                                                n === '…' ? (
                                                    <span key={`ellipsis-${idx}`} className="text-[11px] px-1 text-gray-400">…</span>
                                                ) : (
                                                    <button
                                                        key={n}
                                                        onClick={() => setPage(n)}
                                                        className={`w-7 h-7 rounded-lg border text-[11px] font-semibold transition-colors ${
                                                            page === n
                                                                ? 'bg-[#A0826D] border-[#A0826D] text-white'
                                                                : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {n}
                                                    </button>
                                                )
                                            )}

                                        <button
                                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                            disabled={page === totalPages}
                                            className="px-2.5 py-1 rounded-lg border text-[11px] border-gray-200 bg-white text-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                        >
                                            Next →
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right: detail + print */}
                    {showDetailPanel && selectedOrder ? (
                        <div className="flex-1 flex flex-col overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between">
                                <div>
                                    <h3 className="text-base font-bold text-gray-800">#{selectedOrder.reference_number}</h3>
                                    <p className="text-xs text-gray-400 mt-0.5">{formatDate(selectedOrder.created_at)}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColor(selectedOrder.order_status)}`}>
                                        {selectedOrder.order_status?.charAt(0).toUpperCase() + selectedOrder.order_status?.slice(1)}
                                    </span>
                                    <button onClick={() => { setShowDetailPanel(false); setSelectedOrder(null) }}
                                        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-sm">✕</button>
                                </div>
                            </div>

                            <div className="flex gap-1 px-6 py-3 bg-white border-b border-gray-100">
                                <button onClick={() => setPrintView('receipt')}
                                    className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${printView === 'receipt' ? 'bg-[#303030] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                    🧾 Receipt
                                </button>
                                <button onClick={() => setPrintView('sticker')}
                                    className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${printView === 'sticker' ? 'bg-[#303030] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                    🏷️ Sticker
                                </button>
                            </div>

                            {loadingDetail ? (
                                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Loading order details...</div>
                            ) : (
                                <div className="flex-1 overflow-y-auto">
                                    {printView === 'receipt' && (
                                        <div className="px-6 pt-5 pb-3">
                                            <ReceiptLayout
                                                order={selectedOrder}
                                                items={orderItems}
                                                transaction={orderTransaction}
                                                branchName={branchName}
                                                branchAddress={branchAddress}
                                                cashier={cashierName}
                                            />
                                            {printerMode === 'bluetooth' && (
                                                <div className={`mt-3 rounded-lg px-3 py-2 text-center text-xs font-medium border ${btStatus === 'connected' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                                                    {btStatus === 'connected'
                                                        ? `Receipt printer: ${btPrinterName}`
                                                        : 'No receipt printer connected — click Receipt Scan above'}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {printView === 'sticker' && (
                                        <div className="px-6 py-5 space-y-3">
                                            <div className="flex items-center justify-between gap-3">
                                                <p className="text-sm text-gray-500">Select items to print stickers for:</p>
                                                {printerMode === 'bluetooth' && (
                                                    <select
                                                        value={stickerCommandMode}
                                                        onChange={e => setStickerCommandMode(e.target.value)}
                                                        className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                        title="Sticker printer command language"
                                                    >
                                                        <option value="tspl">TSPL Label</option>
                                                        <option value="escpos">ESC/POS Receipt</option>
                                                    </select>
                                                )}
                                            </div>
                                            {orderItems.length === 0
                                                ? <p className="text-sm text-gray-400 text-center py-6">No active items found.</p>
                                                : orderItems.map(item => (
                                                    <label key={item.order_item_id}
                                                        className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${selectedStickerItems.has(item.order_item_id) ? 'border-[#C9A687] bg-[#C9A687]/5' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                                                        <input type="checkbox" checked={selectedStickerItems.has(item.order_item_id)} onChange={() => toggleStickerItem(item.order_item_id)} className="mt-0.5 accent-[#A0826D]" />
                                                        <div className="flex-1">
                                                            <p className="text-sm font-semibold text-gray-800">{item.menu_items?.name || 'Item'}</p>
                                                            {item.order_item_variants?.length > 0 && <p className="text-xs text-gray-500">{item.order_item_variants.map(v=>v.item_variants?.variant_name).join(' · ')}</p>}
                                                            {item.order_item_modifiers?.length > 0 && <p className="text-xs text-[#A0826D]">+{item.order_item_modifiers.map(m=>m.item_modifiers?.modifier_name).join(', ')}</p>}
                                                            <p className="text-xs text-gray-400 mt-0.5">Qty: {item.quantity}</p>
                                                        </div>
                                                    </label>
                                                ))
                                            }

                                            {/* Hidden system-print sticker DOM */}
                                            <div ref={stickerRef} style={{ display: 'none' }}>
                                                {orderItems.filter(i => selectedStickerItems.has(i.order_item_id))
                                                    .flatMap(item => Array.from({ length: parseInt(item.quantity,10)||1 }, (_, idx) => (
                                                        <div key={`${item.order_item_id}-${idx}`} style={{ width:'50mm', padding:'4mm', marginBottom:'2mm', border:'1px solid #ccc', fontFamily:'monospace', fontSize:11, pageBreakAfter:'always' }}>
                                                            <div style={{ fontWeight:'bold', fontSize:13 }}>{item.menu_items?.name||'Item'}</div>
                                                            {item.order_item_variants?.length>0 && <div style={{ color:'#555', marginTop:2 }}>{item.order_item_variants.map(v=>v.item_variants?.variant_name).join(' · ')}</div>}
                                                            {item.order_item_modifiers?.length>0 && <div style={{ color:'#A0826D', marginTop:2 }}>+{item.order_item_modifiers.map(m=>m.item_modifiers?.modifier_name).join(', ')}</div>}
                                                            <div style={{ marginTop:4, color:'#888', fontSize:10 }}>#{selectedOrder.reference_number} · {formatFulfillment(selectedOrder.fulfillment_type)}</div>
                                                        </div>
                                                    )))
                                                }
                                            </div>

                                            <div className="bg-gray-50 rounded-xl p-3 text-center">
                                                {printerMode === 'bluetooth' && stickerBtStatus === 'connected'
                                                    ? <p className="text-xs text-green-600">Ready — sends {stickerCommandMode === 'tspl' ? 'TSPL label' : 'ESC/POS'} data to <strong>{stickerBtPrinterName}</strong></p>
                                                    : printerMode === 'bluetooth'
                                                        ? <p className="text-xs text-amber-600">No sticker printer connected — click Sticker Scan above.</p>
                                                        : <p className="text-xs text-gray-400">Each item qty prints as a separate sticker. Set your sticker printer as default in OS settings.</p>
                                                }
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="px-6 py-4 border-t border-gray-200 bg-white">
                                <button onClick={() => handlePrint(printView)}
                                    disabled={loadingDetail || isPrinting || (printView === 'sticker' && selectedStickerItems.size === 0) || (printView === 'receipt' && orderItems.length === 0)}
                                    className={`w-full py-3 rounded-xl font-semibold text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2
                                        ${printView === 'receipt' ? 'bg-[#303030] text-white hover:bg-black' : 'bg-[#A0826D] text-white hover:bg-[#8c705b]'}`}>
                                    {isPrinting && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                                    {printBtnLabel}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="hidden md:flex flex-1 flex-col items-center justify-center text-gray-400 bg-gray-50">                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" className="mb-3 opacity-20">
                                <path fill="currentColor" d="M18 3H6v4H5a3 3 0 0 0-3 3v6a2 2 0 0 0 2 2h2v3h12v-3h2a2 2 0 0 0 2-2v-6a3 3 0 0 0-3-3zM8 5h8v2H8zm0 14v-4h8v4zm10-7a1 1 0 1 1 0-2a1 1 0 0 1 0 2z"/>
                            </svg>
                            <p className="text-sm">Select an order to print</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}
